/**
 * What you could cook, given what's in the house.
 *
 * Ordered by what's about to go off rather than by what's nicest — the point of
 * the screen is to stop food being thrown away, so the recipe that saves the
 * dying spinach goes first even if you're missing an onion.
 */

import { useMemo, useState } from "react";
import type { CSSProperties } from "react";

import { RECIPES } from "../features/recipes/recipes";
import { suggestRecipes } from "../features/recipes/suggestRecipes";
import type { Suggestion } from "../features/recipes/suggestRecipes";
import { useSettings } from "../features/settings/SettingsContext";
import { DIET_LABELS, EATING_LABELS } from "../features/settings/types";
import { techniquesFor } from "../features/techniques/recipeTechniques";
import { findTechnique } from "../features/techniques/techniques";
import type { Technique } from "../features/techniques/types";
import type { FoodItem } from "../features/food/types";
import { Icon } from "./Icon";

import "./RecipesScreen.css";

interface RecipesScreenProps {
  items: FoodItem[];
  today: string;
  /** Opens the "what did you use up?" sheet for this recipe. */
  onCooked: (title: string, used: FoodItem[]) => void;
  /** Opens a technique this recipe is a chance to practise. */
  onTechnique: (technique: Technique) => void;
}

/** Why this recipe is near the top, in words. */
function urgencyLine(suggestion: Suggestion): string | null {
  const { urgentInDays, urgentItem } = suggestion;
  if (urgentItem === null || urgentInDays === null) return null;

  if (urgentInDays < 0) return `Uses ${urgentItem.name} — past its date`;
  if (urgentInDays === 0) return `Uses ${urgentItem.name} — today`;
  if (urgentInDays === 1) return `Uses ${urgentItem.name} — tomorrow`;
  if (urgentInDays <= 5) return `Uses ${urgentItem.name} — ${urgentInDays} days`;
  return null;
}

export function RecipesScreen({ items, today, onCooked, onTechnique }: RecipesScreenProps) {
  const { settings } = useSettings();
  const [openId, setOpenId] = useState<string | null>(null);

  // Matching every recipe against every item on each keystroke would be waste;
  // it only changes when the inventory or the filters do.
  const suggestions = useMemo(
    () => suggestRecipes(RECIPES, items, settings, today),
    [items, settings, today]
  );

  if (items.length === 0) {
    return (
      <p className="recipes__empty">
        Add some food and suggestions will appear here.
      </p>
    );
  }

  if (suggestions.length === 0) {
    return (
      <p className="recipes__empty">
        Nothing matches what's in the house yet — try adding a few staples, or
        widen the filters in Settings. Currently showing{" "}
        {settings.diet === "anything"
          ? `${settings.cookingExperience}-level recipes`
          : `${DIET_LABELS[settings.diet].toLowerCase()} recipes at ${settings.cookingExperience} level`}
        , ordered for {EATING_LABELS[settings.eating].toLowerCase()}.
      </p>
    );
  }

  return (
    <div className="recipes">
      {suggestions.map((suggestion, index) => {
        const { recipe, have, missing } = suggestion;
        const isOpen = openId === recipe.id;
        const urgency = urgencyLine(suggestion);

        return (
          <article
            className="recipe"
            key={recipe.id}
            style={{ "--card": Math.min(index, 8) } as CSSProperties}
          >
            <button
              className="recipe__head"
              type="button"
              aria-expanded={isOpen}
              onClick={() => setOpenId(isOpen ? null : recipe.id)}
            >
              <span className="recipe__text">
                <span className="recipe__title">{recipe.title}</span>
                {/* Chips rather than one run-on grey line. Each fact gets its
                    own container, which is how you read a recipe card at a
                    glance instead of parsing a sentence: what you're missing,
                    how long, and the two numbers the goal setting ranks on. */}
                <span className="recipe__facts">
                  <span
                    className="recipe__fact"
                    data-kind={missing.length === 0 ? "complete" : "partial"}
                  >
                    {missing.length === 0
                      ? "Have everything"
                      : `${have.length}/${recipe.ingredients.length} ingredients`}
                  </span>
                  <span className="recipe__fact">{recipe.minutes} min</span>
                  <span className="recipe__fact" data-kind="protein">
                    {recipe.protein}g protein
                  </span>
                  <span className="recipe__fact">{recipe.calories} kcal</span>
                  <span className="recipe__fact recipe__serves">
                    serves {recipe.serves}
                  </span>
                </span>
                {urgency && (
                  <span className="recipe__urgency">
                    <Icon name="clock" className="recipe__urgency-icon" />
                    {urgency}
                  </span>
                )}
              </span>
              <Icon name="chevron" className="recipe__chevron" data-open={isOpen || undefined} />
            </button>

            {isOpen && (
              <div className="recipe__body">
                <ul className="recipe__ingredients">
                  {recipe.ingredients.map((ingredient) => {
                    const short = missing.includes(ingredient);
                    return (
                      <li className="recipe__ingredient" key={ingredient} data-missing={short || undefined}>
                        <Icon
                          name={short ? "close" : "check"}
                          className="recipe__ingredient-icon"
                          label={short ? "Missing" : "Have"}
                        />
                        {ingredient}
                      </li>
                    );
                  })}
                </ul>

                {/* The cooking level changes what is written, not just which
                    recipes are offered. A beginner gets the cue that tells them
                    it's working; an advanced cook gets the technique instead. */}
                <ol className="recipe__steps">
                  {recipe.steps.map((step) => (
                    <li key={step.text}>
                      {step.text}
                      {settings.cookingExperience === "beginner" && step.detail && (
                        <span className="recipe__detail">{step.detail}</span>
                      )}
                    </li>
                  ))}
                </ol>

                {settings.cookingExperience === "advanced" && recipe.notes && (
                  <div className="recipe__notes">
                    <h3 className="recipe__notes-title">Notes</h3>
                    <ul>
                      {recipe.notes.map((note) => (
                        <li key={note}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {recipe.tags.length > 0 && (
                  <p className="recipe__tags">{recipe.tags.join(" · ")}</p>
                )}

                {/* What this one is a chance to practise. Teaching at the
                    moment of use beats teaching in a separate mode — this is
                    the screen you're on when you're about to do the thing. */}
                {techniquesFor(recipe.id).length > 0 && (
                  <div className="recipe__techniques">
                    <h3 className="recipe__techniques-title">Good practice for</h3>
                    <div className="recipe__technique-chips">
                      {techniquesFor(recipe.id).map((id) => {
                        const technique = findTechnique(id);
                        if (!technique) return null;
                        return (
                          <button
                            className="recipe__technique"
                            key={id}
                            type="button"
                            onClick={() => onTechnique(technique)}
                          >
                            {technique.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Only offered when you actually have something it uses —
                    there is nothing to take out of the fridge otherwise. */}
                {have.length > 0 && (
                  <button
                    className="recipe__cooked"
                    type="button"
                    onClick={() => onCooked(recipe.title, have)}
                  >
                    I cooked this
                  </button>
                )}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}
